import React from 'react';

import { BrandColor } from '@styleseat/brand';
import { Box } from '@styleseat/ui';
import { NotificationsAlertCircleIcon } from '../../../../ui/Icons';
import {
  BodyText,
  Bullet,
  Button,
  HeaderText,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
  Stack,
} from '../../../../ui';

export type ErrorStateProps = {
  loading: boolean;
  onTryAgainPress: () => void;
  onUseOtherPaymentPress: () => void;
  onClose: () => void;
};

type BaseErrorStateProps = {
  title: string;
  showOtherPaymentButton?: boolean;
} & ErrorStateProps;

const BaseErrorState: React.FC<BaseErrorStateProps> = ({
  title,
  loading,
  showOtherPaymentButton = false,
  onTryAgainPress,
  onUseOtherPaymentPress,
  onClose,
  children,
}) => (
  <Stack flex={1}>
    {/** header */}
    <PortalOverlayHeader
      hideBottomBorder
      renderRightAction={() => (
        <Box paddingRight={20} paddingTop={20}>
          <PortalOverlayHeaderCloseBtn
            noPadding
            size={18}
            onCloseRequest={onClose}
            testID="tap-to-pay-error__close-button"
          />
        </Box>
      )}
    />
    {/** body */}
    <Stack
      flex={1}
      paddingX={20}
      paddingTop={80}
      justifyContent="flex-start"
    >
      {/** Icon */}
      <Box
        justifyContent="center"
        alignItems="center"
        paddingBottom={24}
      >
        <Box
          minWidth={56}
          maxWidth={56}
          minHeight={56}
          maxHeight={56}
          borderRadius={28}
          showBoxShadow
          justifyContent="center"
          alignItems="center"
        >
          <NotificationsAlertCircleIcon
            height={32}
            width={32}
            color={BrandColor.Red}
            borderColor={BrandColor.Red}
          />

        </Box>
      </Box>
      {/** title */}
      <Box paddingBottom={16} alignItems="center" testID="tap-to-pay-error__header-text">
        <Box maxWidth={327}>
          <HeaderText level={1} fontSize={24} textAlign="center">{title}</HeaderText>
        </Box>
      </Box>
      {/** content */}
      <Box>
        {children}
      </Box>
      {/** CTA section */}
      <Stack paddingTop={40}>
        <Button
          appearance="primary-neue"
          text="Try Again"
          loading={loading}
          onClick={onTryAgainPress}
          testID="tap-to-pay-error__try-again-button"
        />
        {showOtherPaymentButton && (
          <Box paddingTop={6}>
            {/** Note - the link-lowercase button style has a built-in padding of 18px at the top
             * The desire is to have 24px of spacing between the top button and the link
             */}
            <Button
              appearance="link-lowercase"
              text="Use another payment method"
              onClick={onUseOtherPaymentPress}
              testID="tap-to-pay-error__use-another-method-button"
              style={{
                color: BrandColor.Metal,
              }}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  </Stack>
);

export const TransactionErrorState: React.FC<ErrorStateProps> = props => (
  <BaseErrorState
    title="Transaction Error"
    {...props}
  >
    <Box paddingTop={8}>
      <Stack
        space={12}
        testID="tap-to-pay-error__txn-error-text"
        color={BrandColor.SuperLight}
        borderRadius={8}
        padding={24}
      >
        <BodyText
          color={BrandColor.BackInBlack}
          appearance="normal"
          weight="bold"
        >
          Try the following:
        </BodyText>
        <Bullet bulletColor={BrandColor.Metal}>
          <BodyText appearance="medium" color={BrandColor.Metal}>Check your internet connection</BodyText>
        </Bullet>
        <Bullet bulletColor={BrandColor.Metal}>
          <BodyText appearance="medium" color={BrandColor.Metal}>Give location access to the app</BodyText>
        </Bullet>
        <Bullet bulletColor={BrandColor.Metal}>
          <BodyText appearance="medium" color={BrandColor.Metal}>Wait for a connection to the payment reader</BodyText>
        </Bullet>
      </Stack>
    </Box>
  </BaseErrorState>
);

export const PaymentErrorState: React.FC<ErrorStateProps> = props => (
  <BaseErrorState
    title="Payment Error"
    showOtherPaymentButton
    {...props}
  >
    <BodyText
      color={BrandColor.Metal}
      appearance="normal"
      textAlign="center"
      testID="tap-to-pay-error__payment-error-text"
    >
      Unable to process payment at this time. Please try again or use another payment method.
    </BodyText>
  </BaseErrorState>
);

export const CardErrorState: React.FC<ErrorStateProps> = props => (
  <BaseErrorState
    title="Card Failed"
    showOtherPaymentButton
    {...props}
  >
    <BodyText
      color={BrandColor.Metal}
      appearance="normal"
      textAlign="center"
      testID="tap-to-pay-error__card-error-text"
    >
      There was an error processing the payment. Please try again or enter another payment method.
    </BodyText>
  </BaseErrorState>
);

export const OngoingCallErrorState: React.FC<ErrorStateProps> = props => (
  <BaseErrorState
    title="Payments cannot be processed during a phone call or meeting"
    showOtherPaymentButton
    {...props}
  >
    <BodyText
      color={BrandColor.Metal}
      appearance="normal"
      textAlign="center"
      testID="tap-to-pay-error__ongoing-call-error-text"
    >
      Please leave any call or meeting you may be in
      before attempting to check out using Tap to Pay
      on iPhone
    </BodyText>
  </BaseErrorState>
);
